'use client';

import type { BookingTypeSummary, SlotApi } from './types';
import { formatDuration, formatPrice } from './types';

function formatLongDate(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date(iso));
}

function formatTime(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(iso));
}

type Props = {
  step: 1 | 2 | 3;
  bookingType: BookingTypeSummary | null;
  slot: SlotApi | null;
  clientTimezone: string;
};

export function BookingSummaryRail({ step, bookingType, slot, clientTimezone }: Props) {
  return (
    <aside
      className="hidden lg:flex flex-col gap-5 sticky top-24 self-start"
      style={{
        width: 320,
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-2xl)',
        padding: 28,
      }}
    >
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden
          style={{
            display: 'inline-block',
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: 'var(--accent)',
            animation: 'status-pulse 2s ease-in-out infinite',
          }}
        />
        <span
          className="font-mono text-accent"
          style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.18em' }}
        >
          BOOKING IN PROGRESS
        </span>
      </div>

      <div>
        <span
          className="font-mono"
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.14em',
            color: 'var(--text-caption)',
          }}
        >
          01 · TYPE
        </span>
        {bookingType ? (
          <div className="mt-1">
            <div
              className="font-sans text-text"
              style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}
            >
              {bookingType.name}
            </div>
            <div
              className="font-mono text-text-muted mt-1"
              style={{ fontSize: 11, letterSpacing: '0.04em' }}
            >
              {formatDuration(bookingType.durationMin)} · {formatPrice(bookingType.priceCents)}
            </div>
          </div>
        ) : (
          <p className="font-mono text-text-caption mt-1" style={{ fontSize: 12 }}>
            —
          </p>
        )}
      </div>

      <div style={{ height: 1, background: 'var(--border)' }} />

      <div>
        <span
          className="font-mono"
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.14em',
            color: step >= 2 ? 'var(--text-caption)' : 'var(--text-caption)',
          }}
        >
          02 · WHEN
        </span>
        {slot ? (
          <div className="mt-1">
            <div
              className="font-display italic text-text"
              style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.05 }}
            >
              {formatLongDate(slot.startUtc, clientTimezone)}
            </div>
            <div
              className="font-mono text-text-secondary mt-1.5"
              style={{ fontSize: 13, letterSpacing: '0.02em' }}
            >
              {formatTime(slot.startUtc, clientTimezone)} – {formatTime(slot.endUtc, clientTimezone)}
            </div>
          </div>
        ) : (
          <p className="font-mono text-text-caption mt-1" style={{ fontSize: 12 }}>
            —
          </p>
        )}
      </div>

      <div style={{ height: 1, background: 'var(--border)' }} />

      <div>
        <span
          className="font-mono"
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.14em',
            color: 'var(--text-caption)',
          }}
        >
          03 · DETAILS
        </span>
        <p className="font-mono text-text-caption mt-1" style={{ fontSize: 12 }}>
          {step === 3 ? 'Tell us about you →' : '—'}
        </p>
      </div>

      <p
        className="font-mono text-text-caption mt-auto"
        style={{ fontSize: 10, letterSpacing: '0.06em', lineHeight: 1.6 }}
      >
        Alejandro replies within 24 hours.
        <br />
        Cancel anytime via the email link.
      </p>
    </aside>
  );
}
